import { GoogleOAuthProvider } from '@react-oauth/google';
import 'antd/dist/antd.css';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import './App.scss';
import './antd-custom.scss';
import './design-system.scss';
import api from './api/api';
import { returnErrorPage } from './components/Fallbacks/ErrorScreenHandler';
import { GOOGLE_CLIENT_ID, PUBLIC_INFO_PAGES } from './constants/config';
import DashboardLayout from './pages/Dashboard/DashboardLayout';
import ORPLayout from './pages/ORP/Layout';
import UpgradationLayout from './pages/Upgradation/UpgradationLayout';
import { authActions, fetchUserDetails } from './store/auth';
import { deleteLocalData, errorHandler, getLocalData, setAxiosHeaders, setLocalData } from './utils/helper';
import { trackData } from './utils/trackers';
import Loader from './components/Loader/Loader';
import { Modal } from 'antd';
import Router from './router/Router';
import useBrokerConfig from './hooks/useBrokerConfig';
import useAppDownloadModal from './hooks/useAppDownloadModal';
import AppDownloadModal from './components/Modals/AppDownloadModal';
import { dataActions } from './store/data';
import PrivacyPolicyLayout from './pages/PrivacyPolicy/Layout';
import LandingLogin from './pages/Login/LandingLogin/LandingLogin';
import AccessSuspended from './pages/AccessSuspended/AccessSuspended';
import { getEvenFeature } from './hooks/useEvenRedirect';

if (window.location.pathname.startsWith('/app-review')) {
  import('./pages/AppRedirect/AppRedirect');
}

const App = (props: any) => {
  const dispatch = useDispatch(),
    navigate = useNavigate(),
    [searchParams, setSearchParams] = useSearchParams(),
    [noUserFound, setNoUserFound] = useState(false),
    [hasBrokerConfigLoaded, setHasBrokerConfigLoaded] = useState(false),
    { loading, theme, customUrls, fetchBrokerConfig } = useBrokerConfig(),
    email = useSelector((state: any) => state.auth.email),
    isAuthenticated = useSelector((state: any) => state.auth.isLoggedIn),
    userData = useSelector((state: any) => state.auth),
    uhi = useSelector((state: any) => state.auth.uhi),
    mobileAppLinks = useSelector((state: any) => state.theme?.mobileAppLinks),
    isAppAccessSuspended = useSelector(
      (state: any) => state.theme?.paymentEnforcementConfig?.temporaryAppAccessSuspension === true
    ),
    hasAppLinks = (mobileAppLinks?.ios || mobileAppLinks?.android) && mobileAppLinks?.is_enabled,
    hasErrors = useSelector((state: any) => state.error.id),
    location = useLocation(),
    isUHIActive = location.pathname.includes('upgrade-policy') ? true : false,
    isSQ = location.pathname.includes('suraksha-qr') ? true : false,
    isAppReviewRoute = location.pathname.startsWith('/app-review'),
    isAppStoreRoute = location.pathname.startsWith('/app-redirect') || isAppReviewRoute,
    isRedirectRoute = location.pathname.startsWith('/redirect') || isAppStoreRoute,
    isRetailActive = location.pathname.includes('retail') || location.pathname.includes('wellness') ? true : false,
    isGiftCardsActive = location.pathname.startsWith('/gift-cards'),
    evenFeature = location.pathname.startsWith('/even/') ? getEvenFeature(location.pathname) : undefined,
    // Embedded partner apps (Hubble, Even) render inside the dashboard shell, sized to fill the space it leaves.
    isEmbeddedAppActive = isGiftCardsActive || !!evenFeature,
    errorPages = ['403'],
    errorCode = location.pathname.split('/')[1],
    isFullErrorPage = errorPages.includes(errorCode),
    { showAppDownloadModal, handleAppDownloadModalClose } = useAppDownloadModal(),
    navigateToHome = () => {
      navigate('/home');
    },
    logoutHandler = () => {
      api.profile
        .logout()
        .then((res) => {
          dispatch(authActions.logout());
          ssoLogin();
        })
        .catch((err) => {
          errorHandler(err?.response?.data);
        });
    },
    ssoLogin = () => {
      api.profile
        .ssoLogin({
          access_token: searchParams.get('access_token'),
          ...(searchParams.get('oid') && { oid: searchParams.get('oid') }),
          ...(searchParams.get('bid') && { bid: searchParams.get('bid') }),
        })
        .then((res) => {
          if (res?.access_token && res?.refresh_token) {
            navigateToHome();
            dispatch(authActions.login({ accessToken: res?.access_token, refreshToken: res?.refresh_token }));
          }
        })
        .catch((err) => {
          setSearchParams({});
          if (err?.response?.data?.message?.toLowerCase() === 'invalid user or not active yet') {
            setNoUserFound(true);
            dispatch({ type: 'ERROR', payload: { id: 4001 } });
          }
        });
    },
    targetURLCheck = (url: string, response?: any) => {
      if (getLocalData(url)) {
        // if (response?.roles?.includes('employer')) {
        //   if (getLocalData('activeMode')) {
        //     dispatch(authActions.setActiveMode(getLocalData('activeMode')));
        //   } else if (EMPLOYEE_URLS.filter((item: any) => getLocalData(url)?.includes(item))?.length === 1) {
        //     dispatch(authActions.setActiveMode('employee'));
        //   } else {
        //     dispatch(authActions.setActiveMode('employer'));
        //   }
        // } else {
        //   dispatch(authActions.setActiveMode('employee'));
        // }
        navigate(getLocalData(url));
        deleteLocalData(url);
      }
    },
    authCheck = () => {
      if (!!searchParams.get('access_token') && (!!searchParams.get('oid') || !!searchParams.get('bid'))) {
        if (isAuthenticated) {
          Modal.confirm({
            title: `Attention Please!`,
            content: 'You are already logged in from an account. Do you wish to logout first?',
            okText: 'Logout',
            cancelText: 'Cancel',
            onOk: () => {
              logoutHandler();
              return;
            }
          });
        } else {
          ssoLogin();
          return;
        }
      }
      if (!isAuthenticated && !location.pathname.includes('password') && !location.pathname.includes('otp')) {
        if (PUBLIC_INFO_PAGES.some((page) => location.pathname.includes(page))) {
          setLocalData('targetUrl', 'home');
        } else {
          setLocalData('targetUrl', window.location.href.split('/').slice(3).join('/'));
        }
      }
      // fetchBrokerConfig();

      setAxiosHeaders();
      if (isAuthenticated) {
        dispatch(fetchUserDetails() as any);
        setLocalData('targetUrl2', window.location.href.split('/').slice(3).join('/'));
        targetURLCheck('targetUrl2');
        targetURLCheck('targetUrl');
        if (!isRetailActive && Object.keys(uhi).length === 0) {
          api.UHI.renewalStatus()
            .then((res) => {
              dispatch(authActions.setUHI({ uhi: res?.data }));
            })
            .catch((error) => {
              console.log(error);
            })
        }
      }
    };

  useEffect(() => {
    authCheck();
  }, [isAuthenticated])

  const prevIsAuthenticated = useRef(isAuthenticated);
  useEffect(() => {
    if (prevIsAuthenticated.current && !isAuthenticated) {
      fetchBrokerConfig();
    }
    prevIsAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    fetchBrokerConfig();
  }, [])

  useEffect(() => {
    if (email && customUrls.filter((item: any) => item.includes('pagesense'))) {
      var script2 = document.createElement('script');
      script2.type = 'text/javascript';
      script2.innerHTML = `window.pagesense = window.pagesense || [];
      window.pagesense.push(['identifyUser', '${email}']);
      window.pagesense.push(['trackUser', {'role':'${userData?.roles?.includes('employer') ? 'employer' : 'employee'
        }', 'fti':'${uhi?.is_uhi_available}'} ]);`;
      document.head.appendChild(script2);
    }
  }, [email, userData]);

  useEffect(() => {
    trackData('View Page', { URL: location.pathname });
  }, [location]);

  const handleSmartAppBanner = () => {
    const iosLink = mobileAppLinks?.ios;
    if (iosLink) {
      const match = iosLink.match(/id(\d+)/);
      const appId = match ? match[1] : null;

      if (appId) {
        let meta = document.querySelector('meta[name="apple-itunes-app"]');
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('name', 'apple-itunes-app');
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', `app-id=${appId}`);
      }
    }
  };

  useEffect(() => {
    handleSmartAppBanner();
  }, [mobileAppLinks]);

  useEffect(() => {
    document.title = theme.title + ': ' + theme.description;
    document.querySelector('meta[name="description"]')?.setAttribute('content', theme.description);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme.primaryColor);
    document.querySelector('meta[name="msapplication-navbutton-color"]')?.setAttribute('content', theme.primaryColor);
    document
      .querySelector('meta[name="apple-mobile-web-app-status-bar-style"]')
      ?.setAttribute('content', theme.primaryColor);
    if (!isUHIActive) {
      document.documentElement.style.setProperty('--primary-color', theme.primaryColor);
      document.documentElement.style.setProperty('--primary-color-light', theme.primaryColor + '40');
      document.documentElement.style.setProperty('--primary-color-lighter', theme.primaryColor + '20');
      document.documentElement.style.setProperty('--text-color', theme.isDark ? '#fff' : '#000');
    }
    let link: any = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = theme.favicon;

    // Dynamic manifest
    const androidLink = mobileAppLinks?.android;
    let androidId = null;
    try {
      if (androidLink) {
        const url = new URL(androidLink);
        androidId = url.searchParams.get('id');
      }
    } catch (e) {
      const androidMatch = androidLink?.match(/id=([^&]+)/);
      androidId = androidMatch ? androidMatch[1] : null;
    }

    var myDynamicManifest = {
      name: theme.title,
      short_name: theme.title,
      description: theme.description,
      background_color: '#000000',
      theme_color: theme.primaryColor,
      start_url: '.',
      scope: '.',
      display: 'standalone',
      prefer_related_applications: true,
      related_applications: androidId ? [
        {
          platform: 'play',
          id: androidId,
        }
      ] : [],
      icons: [
        {
          src: theme.favicon,
          sizes: '64x64 32x32 24x24 16x16',
          type: 'image/x-icon',
          purpose: 'any maskable',
        },
        {
          src: theme.favicon,
          sizes: '192x192',
          type: 'image/png',
          purpose: 'any maskable',
        },
        {
          src: theme.favicon,
          sizes: '512x512',
          type: 'image/png',
          purpose: 'any maskable',
        },
      ],
    };
    const stringManifest = JSON.stringify(myDynamicManifest);
    const blob = new Blob([stringManifest], { type: 'application/json' });
    const manifestURL = URL.createObjectURL(blob);
    let maniFest: any = document.querySelector('#my-manifest-placeholder');
    maniFest.href = manifestURL;
    if (theme.logoUrl && !isAppReviewRoute)
      setTimeout(() => {
        setHasBrokerConfigLoaded(true);
      }, 400);
  }, [theme]);

  useEffect(() => {
    if (isAppReviewRoute && !loading) {
      setHasBrokerConfigLoaded(true);
    }
  }, [isAppReviewRoute, loading]);

  useEffect(() => {
    if (isUHIActive) {
      document.documentElement.style.setProperty('--primary-color', '#11A387');
      document.documentElement.style.setProperty('--primary-color-light', '#11A38740');
      document.documentElement.style.setProperty('--text-color', '#fff');
    } else {
      document.documentElement.style.setProperty('--primary-color', theme.primaryColor);
      document.documentElement.style.setProperty('--primary-color-light', theme.primaryColor + '40');
      document.documentElement.style.setProperty('--text-color', theme.isDark ? '#fff' : '#000');
    }
  }, [isUHIActive]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const queryVal = queryParams.get('isPhoneApp');
    if (queryVal) {
      setLocalData('isPhoneApp', queryVal);
    }
    const localVal = getLocalData('isPhoneApp');
    const isPhone = (!!queryVal && queryVal !== 'false') || (!!localVal && localVal !== 'false');
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isPhone) {
      document.body.classList.add('is-phone-app');
      if (isIOS) {
        document.body.classList.add('is-ios-app');
      } else {
        document.body.classList.add('is-android-app');
      }
    } else {
      document.body.classList.remove('is-phone-app');
      document.body.classList.remove('is-ios-app');
      document.body.classList.remove('is-android-app');
    }
  }, []);

  if (isRedirectRoute) {
    if ((isAppStoreRoute || isAuthenticated) && !hasBrokerConfigLoaded) {
      return (
        <Loader loading>
          <></>
        </Loader>
      );
    }

    if (isAppAccessSuspended) {
      return <AccessSuspended />;
    }

    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <Router />
      </GoogleOAuthProvider>
    );
  }

  if (noUserFound) {
    return <div className="login-layout no-user-found">{returnErrorPage(4001)}</div>;
  }

  if (!hasBrokerConfigLoaded) {
    return (
      <Loader loading>
        <></>
      </Loader>
    );
  }

  if (PUBLIC_INFO_PAGES.some((page) => location.pathname.includes(page))) {
    return <PrivacyPolicyLayout />;
  }

  if (isAppAccessSuspended) {
    return <AccessSuspended />;
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      {hasErrors && returnErrorPage(hasErrors)}
      {!hasErrors && (
        <>
          {isUHIActive && (
            <div className="uhi-layout">
              <UpgradationLayout />
            </div>
          )}

          {isRetailActive && (
            <div className="retail-layout">
              {location.pathname.includes('retail') && <ORPLayout />} {/* Retail to be deprecated. */}
              {location.pathname.includes('wellness') && <ORPLayout />}
            </div>
          )}

          {isAuthenticated && !isUHIActive && !isRetailActive && (
            <div className={`layout${isEmbeddedAppActive ? ' layout--embedded-app' : ''}`}>
              <DashboardLayout loading={loading} />
            </div>
          )}
          {!isAuthenticated && !isUHIActive && !isRetailActive && (
            <div className="new-auth-container">
              <div className={`login-layout ${isFullErrorPage ? 'with-full-error' : ''}`}>
                <Router />
              </div>
            </div>
          )}
        </>
      )}
      {hasAppLinks && (
        <AppDownloadModal
          visible={showAppDownloadModal}
          onClose={handleAppDownloadModalClose}
        />
      )}
    </GoogleOAuthProvider>
  );
};

export default App;
